import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { JSXElementConstructor, memo } from "react";
import { useParams } from "react-router-dom";
import * as postPurchasePageContentStyles from "./post-purchase-page-content.module.css";

const ProductItem = memo(function ProductItem({
  mainText,
  subText,
  ...props
}: { mainText: string; subText?: string | undefined } & (
  | { imgSrc: string }
  | { Icon: JSXElementConstructor<any> }
)) {
  return (
    <Flex align="center" dir="row" gap="md">
      <Flex
        align="center"
        bgColor="primary"
        borderColor="secondary"
        borderRadius="xs"
        borderStyle="solid"
        borderWidth="1px"
        justify="center"
        overflow="hidden"
        style={{ minWidth: "40px", height: "40px" }}
      >
        {"imgSrc" in props ? (
          <img
            className={postPurchasePageContentStyles.image}
            height={40}
            src={props.imgSrc}
            style={{ borderRadius: "var(--border-radius-xs)" }}
            width={40}
          />
        ) : (
          <props.Icon className={postPurchasePageContentStyles.backupIcon} />
        )}
      </Flex>
      <Flex dir="column" gap="none">
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {mainText}
        </Text>
        {isDef(subText) && (
          <Text fontSize="xxs" textColor="secondary">
            {subText}
          </Text>
        )}
      </Flex>
    </Flex>
  );
});

export type ProductItemData = {
  mainText: string;
  subText?: string | undefined;
} & ({ imgSrc: string } | { Icon: JSXElementConstructor<any> });

export const PostPurchasePageContent = memo(function PostPurchasePageContent({
  upsellPageName,
  upsellPageId,
  showName,
  productItems,
}: {
  upsellPageName: string;
  upsellPageId?: string;
  showName: boolean;
  productItems?: ProductItemData[];
}) {
  const { t } = useTranslation();
  const teamId = useParams().teamId;

  const handleNavigateToUpsellPage = useHandler(() => {
    if (!teamId || !upsellPageId) {
      return;
    }

    const url = `stores/${teamId}/conversion/post-purchase/${upsellPageId}`;
    window.open(url, "_blank");
  });

  return (
    <>
      {showName && (
        <Flex align="flex-start" dir="row" gap="md" justify="space-between">
          <Flex dir="column" gap="xs" justify="flex-start">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Post-purchase page name")}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {upsellPageName}
            </Text>
          </Flex>
          <RedoButton
            aria-label={t("Open upsell page")}
            hierarchy="tertiary"
            IconLeading={LinkExternalIcon}
            onClick={handleNavigateToUpsellPage}
            size="xs"
          />
        </Flex>
      )}
      {productItems && productItems.length > 0 && (
        <Flex dir="column" gap="xl" justify="flex-start">
          <Text fontSize="md" fontWeight="semibold">
            {t("Products")}
          </Text>
          <Flex dir="column" gap="md" w="full">
            {productItems.map((item, index) => (
              <ProductItem key={index} {...item} />
            ))}
          </Flex>
        </Flex>
      )}
    </>
  );
});
